"use client";

import { useT } from "@/lib/i18n/context";
import { useCatalogLocation } from "@/lib/cms/useCatalogLocation";
import type { WLatestRealizationsContent } from "@/lib/api/cms-window-content";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { wLatestRealizationsTranslations } from "./WLatestRealizations.i18n";
import styles from "./WLatestRealizations.module.css";

/**
 * WLatestRealizations - okno "Zobacz inne realizacje".
 *
 * Grid 3 kart z ostatnich realizacji (`c_dedicated_realizations`) z calej domeny+jezyka.
 * Filtruje z listy biezacy article (`?article_id=N` z URL) zeby na widoku realizacji
 * nie pokazywac tej ktora aktualnie jest oglądana. Backend zwraca N+1 pozycji (config
 * `count_last` default 4), po odfiltrowaniu current pokazujemy pierwsze 3.
 *
 * Karta wizualnie spojna z `.card` z `CRealizations` list view - te same tokeny
 * (`--card-*`), aspect-ratio miniatury, badge label + logo overlay.
 */
export function WLatestRealizations({ content, title }: WLatestRealizationsProps) {
  const t = useT(wLatestRealizationsTranslations);
  //Biezacy article ID z URL slug (`.../klientami-15` -> 15) - w Imperial CMS ID
  //artykulu siedzi w extras slug, nie w query params. `useCatalogLocation` parsuje
  //to spojnie z reszta systemu (CatalogLoadMore, adminContext).
  const { articleId: currentArticleId } = useCatalogLocation();

  if (!content || !Array.isArray(content.items) || content.items.length === 0) {
    return null;
  }

  const items = content.items
    .filter((item) => (currentArticleId === null ? true : item.id !== currentArticleId))
    .slice(0, 3);

  if (items.length === 0) return null;

  const heading = title || t.defaultTitle;
  const eyebrow = (content.hero_text ?? "").trim();

  return (
    <section className={styles.root}>
      {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
      <div className={styles.header}>
        <h2 className={styles.heading}>{heading}</h2>
        <span className={styles.line} aria-hidden="true" />
        {content.href ? (
          <a href={content.href} className={styles.showAll}>
            {t.showAll} <span aria-hidden="true">&#8250;</span>
          </a>
        ) : null}
      </div>

      <ul className={styles.grid}>
        {items.map((item) => (
          <li key={item.id} className={styles.card}>
            <a href={item.href} className={styles.cardLink} aria-label={item.name}>
              <div className={styles.cardMedia}>
                {item.picture ? (
                  <img src={item.picture} alt={item.name} className={styles.cardImg} loading="lazy" />
                ) : (
                  <ImagePlaceholder />
                )}
                {item.label ? <span className={styles.cardBadge}>{item.label}</span> : null}
                {item.logo ? (
                  <div className={styles.cardLogo}>
                    <img src={item.logo} alt="" />
                  </div>
                ) : null}
              </div>

              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{item.name}</h3>
                <div className={styles.cardFooter}>
                  {item.sector ? <span className={styles.cardSector}>{item.sector}</span> : <span />}
                  <span className={styles.cardMore}>
                    {t.readMore} <span aria-hidden="true">&rarr;</span>
                  </span>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export type WLatestRealizationsProps = {
  content: WLatestRealizationsContent | null;
  /** Tytul z `imp_cms_window.name` (nadawany przez admina w panelu). */
  title: string;
};

export default WLatestRealizations;
